import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { PrimaryButton, SecondaryButton } from "../../UI";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import ConfirmModal from "../../UI/Modal/confirmModal";
import Loader from "../../UI/Loader/Loader";
import backwardArrow from "../../../assets/icons/backward-arrow.svg";
import { discardProductFeedDraft, getProductFeedById, publishProductFeedConfig, updateProductFeedDraft } from "../../../utils/APIs";
import { ActivityBadge, InfoTip, IssueList, optionsTip, outcomesTip, OutcomeBadge, TabBar, WithInfo } from "./common";
import { POLL_MS, TABS } from "./constants";
import { GLOSSARY } from "./glossary";
import { errorMessage, formatDate } from "./helpers";
import SourcesTab from "./SourcesTab";
import FieldsTab from "./FieldsTab";
import ColumnsTab from "./ColumnsTab";
import PreviewTab from "./PreviewTab";
import ScheduleTab from "./ScheduleTab";
import RunsTab from "./RunsTab";
import { usePreview } from "./usePreview";

const STATUS_TIP = (
    <div className="flex flex-col gap-2">
        {optionsTip(
            undefined,
            {
                published: GLOSSARY.feedStatus.published,
                notPublished: GLOSSARY.feedStatus.notPublished,
                unpublished: GLOSSARY.feedStatus.unpublished,
                unsaved: GLOSSARY.feedStatus.unsaved,
                paused: GLOSSARY.feedStatus.paused,
                running: GLOSSARY.feedStatus.running,
                lastRun: GLOSSARY.feedStatus.lastRun,
                push: "For feeds delivered through the Adobe Target API: the result of the last push. See Schedule & delivery.",
            },
            { published: "Published vN", notPublished: "Not published", unpublished: "Draft has unpublished changes", unsaved: "Unsaved changes", paused: "Paused", running: "Building… / Pushing to Target…", lastRun: "Last run", push: "Target push" }
        )}
        {outcomesTip("Build outcomes:")}
    </div>
);

const ACTIONS_TIP = optionsTip(
    undefined,
    { discard: GLOSSARY.feedStatus.discard, save: GLOSSARY.feedStatus.saveDraft, publish: GLOSSARY.feedStatus.publish },
    { discard: "Discard draft", save: "Save draft", publish: "Publish changes" }
);

// Toasts the result of a build (and its Target push) that just finished.
const announceRun = (run, delivery) => {
    if (!run) return;
    const detail = run.message ? `: ${run.message}` : "";
    if (run.outcome === "failed" || run.outcome === "held") toast.error(`Build ${run.outcome}${detail}`);
    else if (run.push_status === "failed" || run.push_status === "partial") toast.warning(`Build ${run.outcome}, but the Target push ${run.push_status === "failed" ? "failed" : "was partial"}${delivery?.message ? `: ${delivery.message}` : ""}`);
    else toast.success(`Build ${run.outcome}${run.push_status === "pushed" && delivery?.message ? `. ${delivery.message}` : ""}`);
};

const announcePush = (delivery) => {
    if (delivery.status === "failed") toast.error(delivery.message);
    else if (delivery.status === "partial") toast.warning(delivery.message);
    else toast.success(delivery.message);
};

// Feed editor. Edits happen on a local copy of the draft; "Save draft" stores
// it, "Publish changes" validates it, makes it live and starts a build in the
// background; the header shows the build until it finishes.
const FeedEditor = ({ feedId, auth, meta, content, tab, onTabChange, onClose }) => {
    const [feed, setFeed] = useState(null);
    const [draft, setDraft] = useState(null);
    const [name, setName] = useState("");
    const [dirty, setDirty] = useState(false);
    const [validation, setValidation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [publishResult, setPublishResult] = useState(null);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [error, setError] = useState("");
    const preview = usePreview({ auth, feedId });

    // Read by background refreshes, which must never overwrite unsaved edits.
    const dirtyRef = useRef(false);
    useEffect(() => {
        dirtyRef.current = dirty;
    }, [dirty]);

    const applyFeed = (data) => {
        setFeed(data);
        setDraft(data.draft);
        setName(data.name);
        setDirty(false);
    };

    const loadFeed = useCallback(async () => {
        try {
            const result = await getProductFeedById({ ...auth, feedId });
            if (dirtyRef.current) setFeed(result?.data);
            else applyFeed(result?.data);
            setError("");
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [auth.token, auth.organizationId, feedId]);

    useEffect(() => {
        loadFeed();
    }, [loadFeed]);

    // While a build or push is in progress, refresh until it finishes, then
    // say how it went.
    const activity = feed?.activity || null;
    useEffect(() => {
        if (!activity) return undefined;
        const timer = setInterval(loadFeed, POLL_MS);
        return () => clearInterval(timer);
    }, [!!activity, loadFeed]);

    const lastActivity = useRef(null);
    useEffect(() => {
        const previous = lastActivity.current;
        lastActivity.current = activity;
        if (!previous || activity || !feed) return;
        if (previous.trigger) announceRun(feed.last_run, feed.delivery);
        else if (feed.delivery?.message) announcePush(feed.delivery);
    }, [activity, feed]);

    // Warn before closing the tab with unsaved edits.
    useEffect(() => {
        if (!dirty) return undefined;
        const handler = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    const updateDraft = useCallback((updater) => {
        setDraft((previous) => (typeof updater === "function" ? updater(previous) : updater));
        setDirty(true);
    }, []);

    const saveDraft = async ({ quiet = false } = {}) => {
        try {
            setSaving(true);
            const result = await updateProductFeedDraft({ ...auth, feedId, data: { name, config: draft } });
            applyFeed({ ...feed, ...result?.data?.feed });
            setValidation(result?.data?.validation);
            if (!quiet) {
                const errors = result?.data?.validation?.errors?.length || 0;
                if (errors) toast.warn(`Draft saved with ${errors} issue${errors === 1 ? "" : "s"} to fix before publishing.`);
                else toast.success(result?.message);
            }
            return true;
        } catch (e) {
            toast.error(errorMessage(e));
            return false;
        } finally {
            setSaving(false);
        }
    };

    // Publishing starts a build; the header tracks it until it finishes.
    const publish = async () => {
        if (dirty && !(await saveDraft({ quiet: true }))) return;
        try {
            setPublishing(true);
            const result = await publishProductFeedConfig({ ...auth, feedId });
            applyFeed({ ...feed, ...result?.data?.feed });
            setValidation(result?.data?.validation);
            if (result?.data?.run_started) toast.success(result?.message);
            else toast.info(result?.message);
        } catch (e) {
            // 422: validation errors, with details in data. 409: published meanwhile.
            setPublishResult({ ok: false, message: errorMessage(e), ...(e?.data || {}) });
            if (e?.data?.validation) setValidation(e.data.validation);
        } finally {
            setPublishing(false);
        }
    };

    const discard = async () => {
        try {
            setSaving(true);
            const result = await discardProductFeedDraft({ ...auth, feedId });
            applyFeed({ ...feed, ...result?.data });
            setValidation(null);
            setConfirmDiscard(false);
            toast.success(result?.message);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const leave = () => (dirty || preview.building ? setConfirmLeave(true) : onClose());

    if (loading) return <Loader />;
    if (error || !feed) {
        return (
            <div className="flex flex-col gap-3">
                <button type="button" onClick={onClose} className="font-medium text-primary hover:underline flex items-center w-fit">
                    <img src={backwardArrow} alt="" className="w-5 h-5 mr-2" />
                    {content.back_to_saved_tasks}
                </button>
                <p className="text-sm text-red-500">{error || "Feed not found"}</p>
            </div>
        );
    }

    const tabProps = { feed, draft, updateDraft, auth, meta, feedId, dirty, saveDraft, reload: loadFeed };

    return (
        <>
            <button type="button" onClick={leave} className="font-medium text-primary hover:underline flex items-center w-fit">
                <img src={backwardArrow} alt="" className="w-5 h-5 mr-2" />
                {content.back_to_saved_tasks}
            </button>

            <div className="w-full flex items-start lg:items-center justify-between flex-col lg:flex-row gap-4">
                <div className="flex flex-col gap-1 min-w-0 w-full lg:w-auto">
                    <input
                        className="text-xl md:text-2xl font-medium text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-primary focus:outline-none w-full lg:min-w-[420px]"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setDirty(true);
                        }}
                    />
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        {feed.is_published ? (
                            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">Published v{feed.config_version}</span>
                        ) : (
                            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">Not published</span>
                        )}
                        {(feed.has_unpublished_changes || dirty) && <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-medium">{content.unpublished_changes}</span>}
                        {dirty && <span className="text-yellow-700">{content.unsaved_changes}</span>}
                        {!feed.enabled && <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 font-medium">Paused</span>}
                        {activity && (
                            <span className="flex items-center gap-1">
                                <ActivityBadge activity={activity} />
                                {activity.started_at && <span>since {formatDate(activity.started_at)}</span>}
                            </span>
                        )}
                        {feed.last_run && (
                            <span className="flex items-center gap-1">
                                Last run <OutcomeBadge outcome={feed.last_run.outcome} /> {formatDate(feed.last_run.finished_at)}
                            </span>
                        )}
                        {feed.delivery_method === "api" && feed.delivery?.status && (
                            <span className="flex items-center gap-1">
                                Target push <OutcomeBadge outcome={feed.delivery.status} />
                            </span>
                        )}
                        <InfoTip content={STATUS_TIP} wide />
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {feed.is_published && (
                        <SecondaryButton size="sm" disable={saving || publishing || (!dirty && !feed.has_unpublished_changes)} handleClick={() => setConfirmDiscard(true)}>
                            {content.discard_draft}
                        </SecondaryButton>
                    )}
                    <SecondaryButton size="sm" loading={saving} disable={!dirty || publishing} handleClick={() => saveDraft()}>
                        {content.save_draft}
                    </SecondaryButton>
                    <PrimaryButton size="sm" loading={publishing} disable={saving} handleClick={publish}>
                        {content.publish}
                    </PrimaryButton>
                    <InfoTip content={ACTIONS_TIP} wide />
                </div>
            </div>

            {validation && (validation.errors?.length > 0 || validation.warnings?.length > 0) && (
                <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-3">
                    <IssueList title={<WithInfo tip={GLOSSARY.feedStatus.draftChecks}>Draft checks</WithInfo>} errors={validation.errors} warnings={validation.warnings} />
                </div>
            )}

            <TabBar tabs={TABS} active={tab} onChange={onTabChange} />

            <div className="w-full">
                {!draft && <p className="text-sm text-gray-500">This feed has no configuration.</p>}
                {draft && tab === "sources" && <SourcesTab {...tabProps} />}
                {draft && tab === "fields" && <FieldsTab {...tabProps} />}
                {draft && tab === "columns" && <ColumnsTab {...tabProps} />}
                {draft && tab === "preview" && <PreviewTab {...tabProps} preview={preview} />}
                {tab === "schedule" && <ScheduleTab {...tabProps} onFeedChange={(data) => setFeed({ ...feed, ...data })} />}
                {tab === "runs" && <RunsTab {...tabProps} />}
            </div>

            {publishResult && (
                <PrimaryModal title="Can't publish yet" hideModal={() => setPublishResult(null)} widthClass="max-w-3xl">
                    <div className="p-6 flex flex-col gap-4">
                        <p className="text-sm text-red-500">{publishResult.message}</p>
                        <IssueList errors={publishResult.validation?.errors} warnings={publishResult.validation?.warnings} />
                        <div className="flex justify-end gap-3">
                            <PrimaryButton size="sm" handleClick={() => setPublishResult(null)}>Close</PrimaryButton>
                        </div>
                    </div>
                </PrimaryModal>
            )}

            {confirmDiscard && (
                <ConfirmModal
                    title="Discard draft"
                    ConfirmText="Discard"
                    description="Replace the draft with the published configuration? Unpublished changes are lost."
                    hideModal={() => setConfirmDiscard(false)}
                    handleConfirmation={discard}
                    loading={saving}
                />
            )}

            {confirmLeave && (
                <ConfirmModal
                    title={dirty ? "Unsaved changes" : "Preview still building"}
                    ConfirmText={dirty ? "Leave without saving" : "Leave and discard preview"}
                    description={[
                        dirty && "You have unsaved changes to this feed.",
                        preview.building && "A preview is still building, and leaving the editor discards its result.",
                        "Leave anyway?",
                    ].filter(Boolean).join(" ")}
                    hideModal={() => setConfirmLeave(false)}
                    handleConfirmation={onClose}
                />
            )}
        </>
    );
};

export default FeedEditor;
